import { FormEvent, useEffect, useRef, useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { useParams } from 'react-router-dom';
import api from '@/services/api';
import { Button } from '@/components/ui/button';

interface CaptureSession {
  status: string;
  draftText: string;
  flight: {
    flightNo: string;
    flightDate: string | null;
    departure: string;
    arrival: string;
    aircraftType: string;
    aircraftRegistration: string;
    crewRole: string;
    plannedArrivalAt: string | null;
  };
}

interface FlightConfirmationForm {
  aircraftRegistration: string;
  crewRole: string;
  actualArrivalAt: string;
}

export default function PostFlightCapturePage() {
  const { token } = useParams<{ token: string }>();
  const [session, setSession] = useState<CaptureSession | null>(null);
  const [content, setContent] = useState('');
  const [confirmed, setConfirmed] = useState(true);
  const [flightConfirmation, setFlightConfirmation] = useState<FlightConfirmationForm>({
    aircraftRegistration: '',
    crewRole: '',
    actualArrivalAt: '',
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const lastPersistedDraft = useRef('');

  useEffect(() => {
    if (!token) return;
    api.get(`/notes/capture/${token}/`)
      .then((response) => {
        const nextSession = response.data.data as CaptureSession;
        setSession(nextSession);
        const draftText = nextSession.draftText || '';
        setContent(draftText);
        lastPersistedDraft.current = draftText;
        setFlightConfirmation({
          aircraftRegistration: nextSession.flight.aircraftRegistration || '',
          crewRole: nextSession.flight.crewRole || '',
          actualArrivalAt: '',
        });
      })
      .catch((error) => setMessage(error.response?.data?.message || '链接已失效或已使用'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!token || loading || content === lastPersistedDraft.current) return;

    const timer = window.setTimeout(() => {
      api.patch(`/notes/capture/${token}/`, { draftText: content })
        .then(() => {
          lastPersistedDraft.current = content;
        })
        .catch(() => {
          // Keep the local text. Submit still includes the full note content.
        });
    }, 800);

    return () => window.clearTimeout(timer);
  }, [content, loading, token]);

  const updateFlightConfirmation = (field: keyof FlightConfirmationForm, value: string) => {
    setConfirmed(true);
    setFlightConfirmation((current) => ({ ...current, [field]: value }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!content.trim() || !token || !session) return;

    const changes: Partial<FlightConfirmationForm> = {};
    if (flightConfirmation.aircraftRegistration.trim() !== (session.flight.aircraftRegistration || '').trim()) {
      changes.aircraftRegistration = flightConfirmation.aircraftRegistration.trim();
    }
    if (flightConfirmation.crewRole.trim() !== (session.flight.crewRole || '').trim()) {
      changes.crewRole = flightConfirmation.crewRole.trim();
    }
    if (flightConfirmation.actualArrivalAt) {
      changes.actualArrivalAt = flightConfirmation.actualArrivalAt;
    }

    setSubmitting(true);
    try {
      await api.post(`/notes/capture/${token}/`, {
        content,
        confirmed,
        ...(Object.keys(changes).length > 0 ? { flightConfirmation: changes } : {}),
      });
      setMessage('已保存。AI 会在后台自动整理这条笔记。');
    } catch (error: any) {
      setMessage(error.response?.data?.message || '保存失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" />加载中</div>;
  }
  if (!session || message) {
    return <main className="mx-auto flex min-h-screen max-w-md items-center p-6"><p className="w-full text-center text-muted-foreground">{message || '链接已失效或已使用'}</p></main>;
  }

  const { flight } = session;
  return (
    <main className="mx-auto min-h-screen max-w-lg bg-background p-5 pt-10">
      <p className="text-sm text-muted-foreground">落地后随手记</p>
      <h1 className="mt-1 text-xl font-semibold">{flight.flightNo || '本次飞行'} {flight.departure && flight.arrival ? `${flight.departure} → ${flight.arrival}` : ''}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{[flight.flightDate, flight.aircraftType, flight.aircraftRegistration, flight.crewRole].filter(Boolean).join(' · ')}</p>
      <form className="mt-8 space-y-4" onSubmit={submit}>
        <section className="rounded-lg border border-border p-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
            <input
              checked={confirmed}
              className="h-4 w-4 accent-primary"
              onChange={(event) => setConfirmed(event.target.checked)}
              type="checkbox"
            />
            确认系统识别的本次飞行
          </label>
          <details className="mt-3 text-sm">
            <summary className="cursor-pointer text-muted-foreground">信息有变化？可修正机号、岗位或实际到达时间</summary>
            <div className="mt-3 grid gap-3">
              <label className="grid gap-1">
                <span className="text-muted-foreground">机号</span>
                <input
                  className="h-9 rounded-md border border-input bg-background px-3 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!confirmed}
                  maxLength={50}
                  onChange={(event) => updateFlightConfirmation('aircraftRegistration', event.target.value)}
                  value={flightConfirmation.aircraftRegistration}
                />
              </label>
              <label className="grid gap-1">
                <span className="text-muted-foreground">岗位</span>
                <input
                  className="h-9 rounded-md border border-input bg-background px-3 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!confirmed}
                  maxLength={50}
                  onChange={(event) => updateFlightConfirmation('crewRole', event.target.value)}
                  value={flightConfirmation.crewRole}
                />
              </label>
              <label className="grid gap-1">
                <span className="text-muted-foreground">实际到达时间</span>
                <input
                  className="h-9 rounded-md border border-input bg-background px-3 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!confirmed}
                  onChange={(event) => updateFlightConfirmation('actualArrivalAt', event.target.value)}
                  type="datetime-local"
                  value={flightConfirmation.actualArrivalAt}
                />
              </label>
              <p className="text-xs text-muted-foreground">修正信息会单独留档，不覆盖 E-Flight 的计划数据。</p>
            </div>
          </details>
        </section>
        <label className="text-sm font-medium" htmlFor="capture-note">想到什么就写什么，30 秒即可</label>
        <textarea id="capture-note" value={content} onChange={(event) => setContent(event.target.value)} placeholder="例如：今天高温大重量，起飞性能余度要更早确认……" className="min-h-48 w-full rounded-md border border-input bg-background px-3 py-2 text-base shadow-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50" autoFocus />
        <Button className="w-full" disabled={!content.trim() || submitting} type="submit">
          {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
          保存笔记
        </Button>
        {!confirmed && <p className="text-center text-xs text-muted-foreground">不确认航班信息也可以只保存笔记。</p>}
      </form>
    </main>
  );
}
